import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../tokens/api-tokens';
import {
  BulkApproveEnrollmentRequest,
  BulkDeclineEnrollmentRequest,
  EnrollmentQueueCountResponse,
  EnrollmentQueueItem,
  PagedList
} from '../models/enrollment-queue.model';

export interface GetEnrollmentCountParams {
  ccode?: string;
}

export interface GetEnrollmentsParams {
  ccode?: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  enrollmentDateFrom?: string;
  enrollmentDateTo?: string;
  orderByField?: string;
  orderBy?: 'asc' | 'desc';
}

/** Which list the Cancellations tab shows: requests to review, or completed cancellations. */
export type CancellationQueueExportMode = 'AwaitingReview' | 'Completed';

/** The Enrollments tab's filters and sort, as the list sends them (no paging). */
export type EnrollmentQueueExportTab = Omit<GetEnrollmentsParams, 'page' | 'pageSize'>;

/** The Cancellations tab's mode, filters and sort, as its list sends them (no paging). */
export interface CancellationQueueExportTab {
  mode: CancellationQueueExportMode;
  ccode?: string;
  search?: string;
  cancelDateFrom?: string;
  cancelDateTo?: string;
  orderByField?: string;
  orderBy?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class EnrollmentQueueService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  getEnrollments(mcode: string, params: GetEnrollmentsParams): Observable<PagedList<EnrollmentQueueItem>> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    const status = params.status?.trim();
    if (status) {
      httpParams = httpParams.set('status', status);
    }

    if (params.enrollmentDateFrom) {
      httpParams = httpParams.set('enrollmentDateFrom', params.enrollmentDateFrom);
    }
    if (params.enrollmentDateTo) {
      httpParams = httpParams.set('enrollmentDateTo', params.enrollmentDateTo);
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http.get<PagedList<EnrollmentQueueItem>>(`${this.apiUrl}/client/${mcode}/enrollments`, {
      params: httpParams
    });
  }

  /** GET .../enrollments/count — Pending rows in the queue view (no date window). */
  getEnrollmentCount(
    mcode: string,
    params?: GetEnrollmentCountParams
  ): Observable<EnrollmentQueueCountResponse> {
    let httpParams = new HttpParams();
    const ccode = params?.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }
    return this.http.get<EnrollmentQueueCountResponse>(`${this.apiUrl}/client/${mcode}/enrollments/count`, {
      params: httpParams
    });
  }

  /** POST .../enrollment-queue/export?list=Enrollments — 202 queued (link emailed); 400 when nothing matches. Same filters and sort as {@link getEnrollments}. */
  exportEnrollments(mcode: string, params: EnrollmentQueueExportTab): Observable<{ message: string }> {
    let httpParams = new HttpParams().set('list', 'Enrollments');

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    const status = params.status?.trim();
    if (status) {
      httpParams = httpParams.set('status', status);
    }

    if (params.enrollmentDateFrom) {
      httpParams = httpParams.set('dateFrom', params.enrollmentDateFrom);
    }
    if (params.enrollmentDateTo) {
      httpParams = httpParams.set('dateTo', params.enrollmentDateTo);
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.postEnrollmentQueueExport(mcode, httpParams);
  }

  /** POST .../enrollment-queue/export?list=Cancellations — 202 queued (link emailed); 400 when nothing matches. Same filters and sort as the tab's list. */
  exportCancellations(mcode: string, params: CancellationQueueExportTab): Observable<{ message: string }> {
    let httpParams = new HttpParams().set('list', 'Cancellations').set('mode', params.mode);

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (params.cancelDateFrom) {
      httpParams = httpParams.set('dateFrom', params.cancelDateFrom);
    }
    if (params.cancelDateTo) {
      httpParams = httpParams.set('dateTo', params.cancelDateTo);
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.postEnrollmentQueueExport(mcode, httpParams);
  }

  /** Both tabs export through one endpoint; the `list` parameter says which. */
  private postEnrollmentQueueExport(mcode: string, httpParams: HttpParams): Observable<{ message: string }> {
    const url = `${this.apiUrl}/client/${mcode}/enrollment-queue/export`;
    return this.http.post<{ message: string }>(url, null, { params: httpParams });
  }

  /** POST .../enrollments/approve — 204 No Content. */
  bulkApproveEnrollments(mcode: string, body: BulkApproveEnrollmentRequest): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/enrollments/approve`, body)
      .pipe(map(() => undefined));
  }

  /** POST .../enrollments/decline — 204 No Content. */
  bulkDeclineEnrollments(mcode: string, body: BulkDeclineEnrollmentRequest): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/enrollments/decline`, body)
      .pipe(map(() => undefined));
  }
}
